import { api } from './client';

/**
 * Platform Admin Блок B — глобальні довідники super-admin розділу /platform.
 *
 * Endpoint'и не tenant-scoped: auth через токен платформенного власника,
 * БЕЗ X-Tenant-Id (backend ігнорує header для /platform/refs/*). Тому ці
 * виклики впливають на ВСІХ клієнтів — як попереджає PlatformLayout.
 */

/* ----------------------------- Валюти ----------------------------- */

export interface Currency {
  id: string;
  code: string;
  numeric_code: string | null;
  name: string;
  name_short: string | null;
}

export interface CurrencyCreate {
  code: string;
  numeric_code?: string | null;
  name: string;
  name_short?: string | null;
}

/* ---------------------------- Ставки ПДВ --------------------------- */

export interface VatRate {
  id: string;
  code: string;
  name: string;
  rate_percent: number;
  is_active: boolean;
  sort_order: number;
}

export interface VatRateCreate {
  code: string;
  name: string;
  rate_percent: number;
  is_active: boolean;
  sort_order: number;
}

/* ------------------------------ Банки ----------------------------- */

export interface Bank {
  mfo: string;
  name: string;
  full_name: string | null;
  edrpou: string | null;
  is_active: boolean;
  source: string; // 'NBU' | 'MANUAL' (read-only)
}

export interface BankCreate {
  mfo: string;
  name: string;
  full_name?: string | null;
  edrpou?: string | null;
  is_active: boolean;
}

/* ---------------------- Податкові константи (Блок C) ---------------------- */

export interface SourceConstant {
  code: string;
  value: string; // Decimal as string
  unit: string;
  description: string;
  source: string;
  effective_from: string;
}

export interface ConstantRange {
  id: number;
  code: string;
  effective_from: string;
  value: string;
  unit: string;
  description: string;
  source: string;
  created_at: string | null;
  created_by: string | null;
}

export interface DerivedConstant {
  code: string;
  value: string;
  formula: string;
  source_constants: string[];
  legal_reference: string;
  description: string;
  unit: string;
  category: 'AT_DATE' | 'SNAPSHOT_YEAR_START';
  at_date_used: string;
}

export interface AddRange {
  effective_from: string;
  value: number;
  unit: string;
  description: string;
  source: string;
}

/* ------------------- Класифікатори (моніторинг) ------------------- */

export interface ClassifierStatus {
  key: string;
  title: string;
  description: string;
  code_count: number;
  last_updated: string | null;
  seed_source: string | null;
  current_version: string;
  source_url: string;
  change_frequency: string;
  is_current: boolean;
  status: 'OK' | 'CHECK' | 'EMPTY';
}

export const platformClassifiers = {
  list: () =>
    api.get<ClassifierStatus[]>('/platform/classifiers').then((r) => r.data),
};

/* ------------------- Виробничий календар ------------------- */

export interface CalendarYearSummary {
  year: number;
  scenario: 'PEACETIME' | 'WARTIME';
  is_wartime: boolean;
  holidays: number;
  workday_overrides: number;
}

export interface CalendarDay {
  id: string;
  day_date: string;
  day_type: string;
  is_wartime_override: boolean;
  holiday_name: string | null;
  seed_source: string;
}

/* ------------------- Ознаки доходів НДФЛ (4ДФ) ------------------- */

export interface IncomeType {
  id: string;
  code: string;
  name: string;
  is_payroll: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface IncomeTypeCreate {
  code: string;
  name: string;
  is_payroll: boolean;
  is_active: boolean;
  sort_order: number;
}

export const platformIncomeTypes = {
  list: () =>
    api.get<IncomeType[]>('/platform/income-types').then((r) => r.data),
  create: (p: IncomeTypeCreate) =>
    api.post<IncomeType>('/platform/income-types', p).then((r) => r.data),
  update: (id: string, p: IncomeTypeCreate) =>
    api.patch<IncomeType>(`/platform/income-types/${id}`, p).then((r) => r.data),
};

/* ------------------- Підстави звільнення (КЗпП) ------------------- */

export interface DismissalGround {
  id: string;
  code: string;
  article: string;
  name: string;
  is_by_employee: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface DismissalGroundCreate {
  code: string;
  article: string;
  name: string;
  is_by_employee: boolean;
  is_active: boolean;
  sort_order: number;
}

export const platformDismissalGrounds = {
  list: () =>
    api.get<DismissalGround[]>('/platform/dismissal-grounds').then((r) => r.data),
  create: (p: DismissalGroundCreate) =>
    api.post<DismissalGround>('/platform/dismissal-grounds', p).then((r) => r.data),
  update: (id: string, p: DismissalGroundCreate) =>
    api
      .patch<DismissalGround>(`/platform/dismissal-grounds/${id}`, p)
      .then((r) => r.data),
};

/* ------------------- Довідники з УТП (бух/податковий облік) ------------------- */

export interface WorktimeCode {
  id: string;
  letter_code: string;
  digit_code: string;
  name: string;
  is_working_time: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface DisabilityReason {
  id: string;
  code: string;
  name: string;
  days_by_employer: number | null;
  is_active: boolean;
  sort_order: number;
}

export interface TaxListItem {
  id: string;
  code: string;
  name: string;
  is_active: boolean;
  sort_order: number;
}

export const platformWorktimeCodes = {
  list: () =>
    api.get<WorktimeCode[]>('/platform/worktime-codes').then((r) => r.data),
};

export const platformDisabilityReasons = {
  list: () =>
    api.get<DisabilityReason[]>('/platform/disability-reasons').then((r) => r.data),
};

export const platformTaxList = {
  list: () => api.get<TaxListItem[]>('/platform/tax-list').then((r) => r.data),
};

/* ------------------- Індекс інфляції (ІСЦ) ------------------- */

export interface InflationIndex {
  id: string;
  year: number;
  month: number;
  value: string;
  source: string;
}

export const platformInflation = {
  list: () =>
    api.get<InflationIndex[]>('/platform/inflation').then((r) => r.data),
  create: (p: { year: number; month: number; value: number; source: string }) =>
    api.post<InflationIndex>('/platform/inflation', p).then((r) => r.data),
  update: (id: string, p: { value: number; source: string }) =>
    api.patch<InflationIndex>(`/platform/inflation/${id}`, p).then((r) => r.data),
};

export const platformCalendar = {
  summary: () =>
    api
      .get<CalendarYearSummary[]>('/platform/calendar/summary')
      .then((r) => r.data),
  days: (year: number, isWartime: boolean) =>
    api
      .get<CalendarDay[]>('/platform/calendar', {
        params: { year, is_wartime: isWartime },
      })
      .then((r) => r.data),
  patchDay: (id: string, p: { day_type?: string; holiday_name?: string | null }) =>
    api.patch<CalendarDay>(`/platform/calendar/${id}`, p).then((r) => r.data),
};

export interface PspCategory {
  code: string;
  name: string;
  multiplier: string;
  legal_reference: string;
  requires_children: boolean;
}

export const platformConstants = {
  list: () =>
    api.get<SourceConstant[]>('/platform/constants').then((r) => r.data),
  derived: () =>
    api.get<DerivedConstant[]>('/platform/constants/derived').then((r) => r.data),
  pspCategories: () =>
    api
      .get<PspCategory[]>('/platform/constants/psp-categories')
      .then((r) => r.data),
  history: (code: string) =>
    api
      .get<ConstantRange[]>(`/platform/constants/history/${code}`)
      .then((r) => r.data),
  addRange: (code: string, p: AddRange) =>
    api
      .post<ConstantRange>(`/platform/constants/${code}/ranges`, p)
      .then((r) => r.data),
};

export const platformRefs = {
  currencies: {
    list: () =>
      api.get<Currency[]>('/platform/refs/currencies').then((r) => r.data),
    create: (p: CurrencyCreate) =>
      api.post<Currency>('/platform/refs/currencies', p).then((r) => r.data),
    update: (id: string, p: CurrencyCreate) =>
      api.patch<Currency>(`/platform/refs/currencies/${id}`, p).then((r) => r.data),
    remove: (id: string) =>
      api.delete(`/platform/refs/currencies/${id}`).then(() => undefined),
  },

  vatRates: {
    list: () =>
      api.get<VatRate[]>('/platform/refs/vat-rates').then((r) => r.data),
    create: (p: VatRateCreate) =>
      api.post<VatRate>('/platform/refs/vat-rates', p).then((r) => r.data),
    update: (id: string, p: VatRateCreate) =>
      api.patch<VatRate>(`/platform/refs/vat-rates/${id}`, p).then((r) => r.data),
  },

  banks: {
    list: () => api.get<Bank[]>('/platform/refs/banks').then((r) => r.data),
    create: (p: BankCreate) =>
      api.post<Bank>('/platform/refs/banks', p).then((r) => r.data),
    update: (mfo: string, p: BankCreate) =>
      api.patch<Bank>(`/platform/refs/banks/${mfo}`, p).then((r) => r.data),
  },
};
